import { useTranslation } from "@sagot/i18n";
import { Badge, EmptyState, ErrorState, Field, Icon, Select, Skeleton, Textarea } from "@sagot/ui";
import { useFormContext, useWatch } from "react-hook-form";
import { useAssignableMembers, useTeams } from "../shared/queries";
import { SettingsCard } from "./settings-ui";
import { LIMITS, type SettingsValues } from "./settings-model";
import { useAppliedRules, type RuleItem, type TierItem } from "./use-applied-rules";

const FAST_PRIORITIES: ReadonlySet<string> = new Set(["urgent", "high"]);

function labelOf(label: { en: string; fil?: string | null }, language: string): string {
  return language === "fil" && label.fil ? label.fil : label.en;
}

function RuleRow({ rule, language }: { rule: RuleItem; language: string }) {
  const { t } = useTranslation();
  const meta = rule.meta;
  const trigger = meta.trigger ?? "keywords";
  const priority = meta.urgent === "true" ? "urgent" : "high";
  return (
    <tr>
      <td className="font-semibold">
        {labelOf(rule.label, language)}
        {meta.locked === "true" ? (
          <span className="mt-0.5 block text-xs font-normal text-fg-muted">
            {t("build.agentSettings.mandatory.locked")}
            {meta.outranks_tiers === "true" ? ` · ${t("build.agentSettings.mandatory.beforeTiers")}` : ""}
          </span>
        ) : null}
      </td>
      <td className="text-fg-muted">{t(`build.agentSettings.mandatory.trigger.${trigger}`, { defaultValue: trigger })}</td>
      <td><Badge tone={priority === "urgent" ? "danger" : "warning"}>{t(`build.agentSettings.mandatory.priority.${priority}`)}</Badge></td>
      <td>
        <div className="flex flex-wrap gap-1">
          {meta.helpline === "true" ? <Badge tone="neutral">{t("build.agentSettings.mandatory.outHelpline")}</Badge> : null}
          {meta.urgent_ticket === "true" ? <Badge tone="neutral">{t("build.agentSettings.mandatory.outTicket")}</Badge> : null}
          {meta.on_call === "true" ? <Badge tone="neutral">{t("build.agentSettings.mandatory.outOnCall")}</Badge> : null}
          {meta.helpline !== "true" && meta.urgent_ticket !== "true" && meta.on_call !== "true" ? <span className="text-fg-subtle">{t("build.agentSettings.mandatory.outNone")}</span> : null}
        </div>
      </td>
    </tr>
  );
}

function RulesTable({ rules, language }: { rules: readonly RuleItem[]; language: string }) {
  const { t } = useTranslation();
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable so keyboard users can scroll it (axe scrollable-region-focusable)
    <div className="overflow-x-auto rounded-md border border-line focus-visible:outline-2 focus-visible:outline-primary" role="region" tabIndex={0} aria-label={t("build.agentSettings.mandatory.rulesScroll")}>
      <table className="table" aria-label={t("build.agentSettings.mandatory.rulesLabel")}>
        <thead>
          <tr>
            <th>{t("build.agentSettings.mandatory.colRule")}</th>
            <th>{t("build.agentSettings.mandatory.colDetected")}</th>
            <th>{t("build.agentSettings.mandatory.colPriority")}</th>
            <th>{t("build.agentSettings.mandatory.colOutOfHours")}</th>
          </tr>
        </thead>
        <tbody>{rules.map((rule) => <RuleRow key={rule.key} rule={rule} language={language} />)}</tbody>
      </table>
    </div>
  );
}

function TiersTable({ tiers, language }: { tiers: readonly TierItem[]; language: string }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">{t("build.agentSettings.mandatory.tiersTitle")}</h3>
      <p className="text-xs text-fg-subtle">{t("build.agentSettings.mandatory.tiersHint")}</p>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable so keyboard users can scroll it (axe scrollable-region-focusable) */}
      <div className="overflow-x-auto rounded-md border border-line focus-visible:outline-2 focus-visible:outline-primary" role="region" tabIndex={0} aria-label={t("build.agentSettings.mandatory.tiersScroll")}>
        <table className="table" aria-label={t("build.agentSettings.mandatory.tiersLabel")}>
          <thead>
            <tr>
              <th>{t("build.agentSettings.mandatory.colTier")}</th>
              <th>{t("build.agentSettings.mandatory.colMenu")}</th>
              <th>{t("build.agentSettings.mandatory.colBasePriority")}</th>
              <th>{t("build.agentSettings.mandatory.colPickup")}</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier) => {
              const priority = tier.meta.priority ?? "normal";
              const fast = FAST_PRIORITIES.has(priority);
              const minutes = tier.meta.first_response_minutes;
              return (
                <tr key={tier.key}>
                  <td className="font-semibold">{labelOf(tier.label, language)}</td>
                  <td>{fast ? t("build.agentSettings.mandatory.skipMenu") : <span className="text-fg-subtle">{t("build.agentSettings.mandatory.noSkip")}</span>}</td>
                  <td><Badge tone={fast ? "warning" : "neutral"}>{t(`build.agentSettings.mandatory.priority.${priority}`, { defaultValue: priority })}</Badge></td>
                  <td className="text-fg-muted">{minutes ? t("build.agentSettings.mandatory.minutes", { count: Number(minutes) }) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RulesSkeleton() {
  return (
    <div className="space-y-2" role="status" aria-label="loading">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

/** A rule alerts the on-call contact out of hours; with nobody set, that alert goes nowhere (HND-001). */
function NoOnCallWarning({ rules }: { rules: readonly RuleItem[] }) {
  const { t } = useTranslation();
  const form = useFormContext<SettingsValues>();
  const onCall = useWatch({ control: form.control, name: "on_call_user_id" });
  if (onCall || !rules.some((rule) => rule.meta.on_call === "true")) return null;
  return (
    <div role="status" data-testid="no-on-call-warning" className="flex items-start gap-2.5 rounded-md border border-warning bg-warning-soft p-3 text-sm">
      <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
      <p>{t("build.agentSettings.mandatory.noOnCallWarning")}</p>
    </div>
  );
}

function Settings({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<SettingsValues>();
  const teams = useTeams();
  const members = useAssignableMembers();
  const helpline = useWatch({ control: form.control, name: "helpline_text" });
  const locked = disabled || teams.isError;
  const teamOptions = (empty: string) => (
    <>
      <option value="">{empty}</option>
      {(teams.data ?? []).map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
    </>
  );
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label={t("build.agentSettings.mandatory.team")} hint={t("build.agentSettings.mandatory.teamHint")}>
        <Select disabled={locked} {...form.register("mandatory_team_id")}>{teamOptions(t("build.agentSettings.escalation.sameAsDefault"))}</Select>
      </Field>
      <Field label={t("build.agentSettings.mandatory.onCall")} hint={members.isError ? t("build.agentSettings.escalation.membersUnavailable") : t("build.agentSettings.mandatory.onCallHint")}>
        <Select disabled={disabled || members.isError} {...form.register("on_call_user_id")}>
          <option value="">{t("build.agentSettings.mandatory.noOnCall")}</option>
          {(members.data ?? []).map((member) => <option key={member.user_id} value={member.user_id}>{member.name}</option>)}
        </Select>
      </Field>
      <div className="md:col-span-2">
        <Field label={t("build.agentSettings.mandatory.helpline")} hint={t("build.agentSettings.mandatory.helplineHint", { count: helpline.length, max: LIMITS.helplineMax })}>
          <Textarea rows={2} maxLength={LIMITS.helplineMax} disabled={disabled} {...form.register("helpline_text")} />
        </Field>
      </div>
      <Field label={t("build.agentSettings.mandatory.priorityTeam")} hint={t("build.agentSettings.mandatory.priorityTeamHint")}>
        <Select disabled={locked} {...form.register("priority_team_id")}>{teamOptions(t("build.agentSettings.mandatory.noPriorityTeam"))}</Select>
      </Field>
    </div>
  );
}

/** Escalation tab: the handovers the pack requires (read-only, locked ones outrank every tier), tier priorities, and who takes them (HND-001, HND-002). */
export function MandatoryHandoverCard({ disabled }: { disabled: boolean }) {
  const { t, i18n } = useTranslation();
  const applied = useAppliedRules();
  const language = i18n.language;
  const data = applied.data;
  const active = data?.key ? data : null;
  return (
    <SettingsCard title={t("build.agentSettings.mandatory.title")} hint={t("build.agentSettings.mandatory.hint")}>
      {applied.isPending ? <RulesSkeleton /> : null}
      {applied.isError ? (
        <ErrorState headingLevel={3} title={t("build.agentSettings.mandatory.errorTitle")} description={t("build.agentSettings.mandatory.errorBody")} onRetry={() => { void applied.refetch(); }} retryLabel={t("build.agentSettings.mandatory.retry")} />
      ) : null}
      {data?.key === null ? (
        <EmptyState icon="shield-check" headingLevel={3} title={t("build.agentSettings.mandatory.emptyTitle")} description={t("build.agentSettings.mandatory.emptyBody")} />
      ) : null}
      {active ? (
        <>
          {active.rules.length > 0 ? <RulesTable rules={active.rules} language={language} /> : <p className="text-sm text-fg-muted">{t("build.agentSettings.mandatory.noRules")}</p>}
          <p className="rounded-md bg-sunken px-3 py-2 text-xs text-fg-muted">{t("build.agentSettings.mandatory.neverChanges")}</p>
          {active.tiers.length > 0 ? <TiersTable tiers={active.tiers} language={language} /> : null}
          <NoOnCallWarning rules={active.rules} />
        </>
      ) : null}
      <Settings disabled={disabled} />
    </SettingsCard>
  );
}
